import Image from 'next/image';
import { Card, Paragraph, Wrap } from '@leafwell/components';
import HeadingBody from 'components/heading-body';
import Line from 'components/line';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { BlockStateLaws } from 'types/block-state-laws';

const StatusCard: React.FC<{
  footer?: { positive: boolean; label: string; key?: string }[];
  info?: { label: string; value: string; extraInfo?: string }[];
  title: string;
  id?: string;
}> = ({ footer, info, title, id }) => (
  <Card
    id={id}
    rounded
    variant="secondary"
    className="grid-rows-card-small border-2"
  >
    <HeadingBody className="pb-3 lg:pb-6" level="4" title={title} />
    {info && (
      <ul className="grid gap-y-3 lg:gap-y-6">
        {info.map(({ label, value, extraInfo }, idx) => (
          <Paragraph
            key={`${id}-${idx}-info`}
            size="small"
            tagName="li"
            className="grid sm:grid-cols-2 sm:justify-between sm:gap-x-3"
          >
            {label}
            <p className="!m-0 sm:justify-self-end sm:text-right">
              <span className="sm:font-medium">{value}</span>
              {extraInfo && (
                <Paragraph
                  tagName="span"
                  size="xsmall"
                  variant="medium"
                  className="pl-2 sm:pl-0 sm:block"
                >
                  {extraInfo}
                </Paragraph>
              )}
            </p>
          </Paragraph>
        ))}
      </ul>
    )}
    {info && footer && <Line />}
    {footer && (
      <ul className="grid gap-y-3">
        {footer.map(({ label, positive }, idx) => (
          <li
            key={`${id}-${idx}-footer`}
            className="grid grid-flow-col gap-x-6 justify-between"
          >
            {label}
            <Image
              src={positive ? '/check-green.svg' : '/not-allowed.svg'}
              alt=""
              height={24}
              width={24}
              className="self-center"
            />
          </li>
        ))}
      </ul>
    )}
  </Card>
);

export default function StateLaws({
  sectionId,
  title,
  legalStatus,
  possession,
  taxes,
  cultivation,
}: Omit<BlockStateLaws, 'acfFcLayout'>) {
  const { translations } = useWordPressContext();

  return (
    <Wrap
      id={sectionId}
      className="md:w-full overflow-hidden mx-auto py-10 md:py-12"
      size="large"
    >
      {title?.length > 0 && (
        <HeadingBody className="text-center" {...{ level: '2', title }} />
      )}
      <div className="grid gap-y-6 sm:gap-x-6 sm:grid-cols-2 lg:auto-cols-fr lg:grid-flow-col xl:gap-x-8">
        <StatusCard
          id="legal-status"
          title={translations['Legal status']}
          footer={
            legalStatus
              ? legalStatus?.status.map(({ label, value }) => ({
                  label,
                  positive:
                    value === 'medical-program' || value === 'fully-legal',
                }))
              : null
          }
        />
        <StatusCard
          id="possession-info"
          title={translations['Possession Limits']}
          info={possession.info}
          footer={
            possession.legal
              ? null
              : [
                  {
                    label: translations['Possession is %s'].replace(
                      '%s',
                      translations['not allowed'],
                    ),
                    positive: false,
                  },
                ]
          }
        />
        {taxes && (
          <StatusCard
            id="state-taxes"
            title={translations['State taxes']}
            info={taxes}
          />
        )}
        {cultivation.show && (
          <StatusCard
            id="cultivation-info"
            title={translations['Cultivation']}
            info={cultivation.legal ? cultivation.info : null}
            footer={[
              {
                label: translations['Cultivation is %s'].replace(
                  '%s',
                  cultivation.legal
                    ? translations['legal']
                    : translations['illegal'],
                ),
                positive: cultivation.legal,
              },
            ]}
          />
        )}
      </div>
    </Wrap>
  );
}
